/* ============================================================================
   Slide 8 — "Adhama Pranayama" — SHARED CONTENT (single source of truth)
   Read by the desktop layout (8_adhamaPranayama.tsx), the practice body
   (8_adhamaPranayama_body.tsx) and the mobile layout (HTLDSlide8Mobile).
   Edit copy ONLY here — English and German side by side; the German object
   must have exactly the same shape as the English one.
   ========================================================================== */
import { ByLang, useL } from "i18n/lang";

export const accent8 = "#e07b39";

export interface Seg { t: string; b?: boolean; em?: boolean; }

const en = {
  meta: {
    breadcrumbPre: "Breath Practice",
    breadcrumbAccent: "Calming the nervous system",
    sanskrit: "अधम प्राणायाम",
    headerTitlePre: "Adhama ",
    headerTitleAccent: "Pranayama",
    titlePre: "Deep Diaphragmatic ",
    titleAccentWord: "Breathing",
    titlePost: "",
    subtitle: "Adham Pranayama · The foundation of all pranayama practice",
    whyLabel: "Why this first?",
    howLabel: "How to practise",
    durationLabel: "Duration",
    /** one-line summary under the title of the portrait practice guide */
    guideSummary: "Inhale 4 · Hold 2 · Exhale 6 — 9 rounds",
  },
  steps: [
    { step: "Sit or lie comfortably", desc: "Spine long, shoulders relaxed, one hand on the belly." },
    { step: "Inhale — 4 counts",      desc: "Breathe slowly into the belly. Feel it rise. Ribs stay still." },
    { step: "Hold — 2 counts",        desc: "Brief, gentle pause at the top. No strain." },
    { step: "Exhale — 6 counts",      desc: "Release slowly. Belly falls. The longer exhale activates the vagus nerve." },
  ] as { step: string; desc: string }[],
  why: [
    { t: "In yogic science, breath and mind are directly linked — " },
    { t: "where the breath goes, the mind follows.", b: true },
    { t: " Adham Pranayama activates the parasympathetic nervous system, lowering cortisol and heart rate, beginning the transition from Beta toward the calm Alpha state." },
  ] as Seg[],
  duration: [
    { t: "Practise " },
    { t: "9 rounds", b: true },
    { t: " before moving to Nadi Shuddhi. The goal is a calm, steady rhythm — not control or force. Let the breath become effortless." },
  ] as Seg[],
  /** text drawn on the breathing-guide canvas */
  canvas: {
    inhale: "INHALE",
    hold: "HOLD",
    exhale: "EXHALE",
    /** phase-bar segment labels (counts are fixed: 4 · 2 · 6) */
    barInhale: "INHALE · 4",
    barHold: "HOLD · 2",
    barExhale: "EXHALE · 6",
    followBreath: "follow the breath",
    pressToBegin: "press ▶ to begin",
    round: (n: number, total: number) => `round ${n} · of ${total}`,
  },
};

export type Slide8Content = typeof en;

const de: Slide8Content = {
  meta: {
    breadcrumbPre: "Atempraxis",
    breadcrumbAccent: "Das Nervensystem beruhigen",
    sanskrit: "अधम प्राणायाम",
    headerTitlePre: "Adhama ",
    headerTitleAccent: "Pranayama",
    titlePre: "Tiefe ",
    titleAccentWord: "Zwerchfellatmung",
    titlePost: "",
    subtitle: "Adham Pranayama · Die Grundlage jeder Pranayama-Praxis",
    whyLabel: "Warum zuerst?",
    howLabel: "So übst du",
    durationLabel: "Dauer",
    guideSummary: "Einatmen 4 · Halten 2 · Ausatmen 6 – 9 Runden",
  },
  steps: [
    { step: "Setz oder leg dich bequem hin", desc: "Wirbelsäule lang, Schultern locker, eine Hand auf dem Bauch." },
    { step: "Einatmen – 4 Zählzeiten",       desc: "Atme langsam in den Bauch. Spüre, wie er sich hebt. Die Rippen bleiben ruhig." },
    { step: "Halten – 2 Zählzeiten",         desc: "Kurze, sanfte Pause oben. Ohne Anstrengung." },
    { step: "Ausatmen – 6 Zählzeiten",       desc: "Lass langsam los. Der Bauch senkt sich. Die längere Ausatmung aktiviert den Vagusnerv." },
  ],
  why: [
    { t: "In der Yogawissenschaft sind Atem und Geist direkt verbunden – " },
    { t: "wohin der Atem geht, folgt der Geist.", b: true },
    { t: " Adham Pranayama aktiviert das parasympathische Nervensystem, senkt Cortisol und Herzfrequenz und leitet den Übergang von Beta zum ruhigen Alpha-Zustand ein." },
  ],
  duration: [
    { t: "Übe " },
    { t: "9 Runden", b: true },
    { t: ", bevor du zu Nadi Shuddhi übergehst. Ziel ist ein ruhiger, gleichmäßiger Rhythmus – keine Kontrolle, kein Zwang. Lass den Atem mühelos werden." },
  ],
  canvas: {
    inhale: "EINATMEN",
    hold: "HALTEN",
    exhale: "AUSATMEN",
    barInhale: "EINATMEN · 4",
    barHold: "HALTEN 2",
    barExhale: "AUSATMEN · 6",
    followBreath: "folge dem Atem",
    pressToBegin: "drücke ▶ zum Starten",
    round: (n: number, total: number) => `Runde ${n} · von ${total}`,
  },
};

export const slide8Content: ByLang<Slide8Content> = { en, de };
export const useSlide8 = () => useL(slide8Content);
